import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2, ArrowLeft, Activity, Shield, Mail, Clock,
} from 'lucide-react';
import PageMeta from '../components/PageMeta';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TARGET_UPTIME = 99.9; // SLO

const SLAPage = () => {
  const [overall, setOverall] = useState(null);
  const [pingMs, setPingMs] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/status/summary`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) return;
        setOverall(d.overall);
        const dbComp = (d.components || []).find(c => c.name === 'Database');
        if (dbComp) setPingMs(dbComp.ping_ms);
      })
      .catch(() => {});
  }, []);

  const isHealthy = overall === 'operational';

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white" data-testid="sla-page">
      <PageMeta
        slug="sla"
        path="/sla"
        title="Service-Level Commitment"
        description="HiBFF's 99.9% uptime target with service credits per availability tier. Live status, scope, measurement methodology, and postmortem promise."
      />
      <header className="px-6 sm:px-12 py-6 max-w-4xl mx-auto">
        <Link to="/trust-center" className="inline-flex items-center gap-2 text-gray-700 hover:text-gray-900" data-testid="sla-back">
          <ArrowLeft size={18} /> <span className="font-medium">Trust Center</span>
        </Link>
      </header>

      <main className="px-6 sm:px-12 max-w-4xl mx-auto pb-20">
        <div className="flex items-center gap-3 mb-2 text-purple-600">
          <CheckCircle2 size={28} />
          <span className="font-semibold text-sm uppercase tracking-wide">Service-level commitment</span>
        </div>
        <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold mb-3" style={{ color: '#1A1209' }}>
          Our promise on uptime
        </h1>
        <p className="text-base text-gray-700 mb-8 max-w-2xl">
          HiBFF commits to a target service availability of <strong>{TARGET_UPTIME}%</strong> per
          calendar month for the production hibff.com platform and API.
          That&apos;s a maximum of about <strong>43 minutes</strong> of downtime per month.
        </p>

        {/* Live status snapshot */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="text-center sm:border-r border-gray-100">
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Target SLO</p>
            <p className="text-3xl font-bold" style={{ color: '#1A1209' }}>{TARGET_UPTIME}%</p>
          </div>
          <div className="text-center sm:border-r border-gray-100">
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Right now</p>
            <p className="text-3xl font-bold" style={{ color: isHealthy ? '#10B981' : '#F59E0B' }}>
              {overall ? overall === 'operational' ? 'Healthy' : overall.charAt(0).toUpperCase() + overall.slice(1) : '…'}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">DB ping</p>
            <p className="text-3xl font-bold" style={{ color: '#1A1209' }}>
              {pingMs != null ? `${pingMs}ms` : '—'}
            </p>
          </div>
        </div>

        {/* What's in scope */}
        <Section title="What's in scope">
          <ul className="space-y-2 text-gray-700">
            <li>· The <strong>hibff.com</strong> production website (excluding cached marketing pages served by Cloudflare)</li>
            <li>· All <strong>/api/*</strong> endpoints on the production API</li>
            <li>· The <strong>HiBFF Android app</strong> — including OTA updates served via Capgo</li>
            <li>· Authentication flows: email + password, Google OAuth, WebAuthn admin login</li>
            <li>· Crisis safety routing (priority infrastructure — separate, stricter targets enforced internally)</li>
          </ul>
        </Section>

        <Section title="What's out of scope">
          <ul className="space-y-2 text-gray-700">
            <li>· Scheduled maintenance windows (announced ≥ 48 hours in advance via the status page)</li>
            <li>· Outages caused by upstream subprocessors when they breach their own SLAs (we credit you anyway — see below)</li>
            <li>· Force-majeure events including DNS-level attacks against shared infrastructure</li>
            <li>· Issues caused by the customer&apos;s own integration code, custom DNS, or local network</li>
          </ul>
        </Section>

        <Section title="How we measure">
          <p className="text-gray-700 mb-3">
            Availability is measured from <code className="bg-gray-100 px-1 rounded text-sm">/api/health</code> — the same endpoint used
            by our load balancer. A request counts as &quot;available&quot; if it returns HTTP 200 within 5 seconds and the database ping inside
            the response is non-null.
          </p>
          <p className="text-gray-700">
            External monitors (UptimeRobot, Pingdom-style) sample the endpoint every 60 seconds from multiple regions.
            Monthly availability = <code className="bg-gray-100 px-1 rounded text-sm">(total_minutes - failed_minutes) / total_minutes × 100</code>.
          </p>
        </Section>

        <Section title="Service credits (B2B Plus contracts)">
          <p className="text-gray-700 mb-3">
            If we miss the {TARGET_UPTIME}% target in a calendar month, customers on a paid B2B Plus contract receive a credit toward the next month&apos;s invoice:
          </p>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left">
                  <th className="px-4 py-2 font-semibold text-gray-700">Monthly availability</th>
                  <th className="px-4 py-2 font-semibold text-gray-700">Service credit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr><td className="px-4 py-2">≥ 99.9 %</td><td className="px-4 py-2 text-emerald-600 font-medium">No credit (within target)</td></tr>
                <tr><td className="px-4 py-2">99.0 % – 99.89 %</td><td className="px-4 py-2">10 % of monthly fee</td></tr>
                <tr><td className="px-4 py-2">95.0 % – 98.99 %</td><td className="px-4 py-2">25 % of monthly fee</td></tr>
                <tr><td className="px-4 py-2">&lt; 95.0 %</td><td className="px-4 py-2">50 % of monthly fee</td></tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Credits are claimed within 30 days of the affected month by emailing business@hibff.com with the customer ID and
            relevant period. Credits do not exceed the customer&apos;s monthly fee.
          </p>
        </Section>

        <Section title="Incidents & postmortems">
          <p className="text-gray-700">
            Every WARN+ event is recorded on our public{' '}
            <Link to="/status" className="text-purple-600 hover:underline">status page</Link>.
            For incidents that breach the SLO, we publish a postmortem within 7 business days
            covering: what happened, when, root cause, immediate fix, follow-up actions, and
            any process changes.
          </p>
        </Section>

        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border border-purple-100 p-6 sm:p-8 text-center mt-10">
          <Shield size={28} className="mx-auto text-purple-600 mb-2" />
          <h2 className="font-semibold text-gray-900 mb-2">Need this in a signed agreement?</h2>
          <p className="text-sm text-gray-700 mb-4">
            We provide a tailored SLA + Data Processing Agreement to every B2B Plus customer.
          </p>
          <a
            href="mailto:business@hibff.com"
            className="inline-flex items-center gap-2 px-5 py-2 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition text-sm font-semibold"
            data-testid="sla-email-procurement"
          >
            <Mail size={14} /> business@hibff.com
          </a>
        </div>

        <p className="text-xs text-gray-500 mt-8 text-center flex items-center justify-center gap-1.5">
          <Clock size={12} /> SLA last reviewed: 25 April 2026.{' '}
          <Link to="/status" className="text-purple-600 hover:underline ml-1 inline-flex items-center gap-1">
            <Activity size={12} /> Live status
          </Link>
        </p>
      </main>
    </div>
  );
};

const Section = ({ title, children }) => (
  <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 mb-6">
    <h2 className="text-base sm:text-lg font-semibold mb-3" style={{ color: '#1A1209' }}>{title}</h2>
    {children}
  </section>
);

export default SLAPage;
